import type { StructuredFilter } from "@media-viewer/shared";

export type Chip = {
  id: string;
  label: string;
  /** Returns a new StructuredFilter with this chip removed. */
  remove: (f: StructuredFilter) => StructuredFilter;
  tone: "neutral" | "include" | "exclude" | "exif" | "favorite" | "warning";
};

type Terms = StructuredFilter["include"];

/**
 * Remove one matched tag from the term at `index`. If that was its last tag,
 * drop the whole term (a tag-derived term with no tags left is meaningless).
 * Other terms are untouched.
 */
function dropMatchedTag(terms: Terms, index: number, slug: string): Terms {
  return (terms ?? [])
    .map((t, j) => (j === index ? { ...t, matchedTags: (t.matchedTags ?? []).filter((m) => m.slug !== slug) } : t))
    .filter((t, j) => j !== index || (t.matchedTags?.length ?? 0) > 0);
}

/** Drop one literal name entry; clear `name` entirely when both lists empty. */
function removeName(f: StructuredFilter, key: "include" | "exclude", index: number): StructuredFilter {
  const cur = f.name ?? {};
  const nextList = (cur[key] ?? []).filter((_, j) => j !== index);
  const other = key === "include" ? cur.exclude ?? [] : cur.include ?? [];
  const next: NonNullable<StructuredFilter["name"]> = {};
  const inc = key === "include" ? nextList : other;
  const exc = key === "include" ? other : nextList;
  if (inc.length > 0) next.include = inc;
  if (exc.length > 0) next.exclude = exc;
  const isEmpty = !next.include?.length && !next.exclude?.length;
  return { ...f, name: isEmpty ? undefined : next };
}

function fmtSec(s: number): string {
  if (s < 60) return `${s}s`;
  if (s % 60 === 0) return `${s / 60}m`;
  return `${Math.floor(s / 60)}m${s % 60}s`;
}

export function filterToChips(filter: StructuredFilter, pinnedKind?: "photo" | "video" | null): Chip[] {
  const chips: Chip[] = [];

  if (filter.kind && filter.kind !== pinnedKind) {
    chips.push({
      id: "kind",
      label: `kind: ${filter.kind}`,
      tone: "neutral",
      remove: (f) => ({ ...f, kind: undefined }),
    });
  }
  if (filter.duration) {
    const { minSec, maxSec } = filter.duration;
    const label =
      minSec != null && maxSec != null
        ? `${fmtSec(minSec)} – ${fmtSec(maxSec)}`
        : minSec != null
          ? `≥ ${fmtSec(minSec)}`
          : maxSec != null
            ? `≤ ${fmtSec(maxSec)}`
            : "";
    if (label) {
      chips.push({
        id: "duration",
        label,
        tone: "neutral",
        remove: (f) => ({ ...f, duration: undefined }),
      });
    }
  }
  if (filter.takenAt) {
    const { from, to } = filter.takenAt;
    const label = from && to ? `taken ${from} → ${to}` : from ? `taken since ${from}` : to ? `taken until ${to}` : "";
    if (label) chips.push({ id: "takenAt", label, tone: "neutral", remove: (f) => ({ ...f, takenAt: undefined }) });
  }
  if (filter.addedAt) {
    const { from, to } = filter.addedAt;
    const label = from && to ? `added ${from} → ${to}` : from ? `added since ${from}` : to ? `added until ${to}` : "";
    if (label) chips.push({ id: "addedAt", label, tone: "neutral", remove: (f) => ({ ...f, addedAt: undefined }) });
  }
  // A term that matched tags shows one chip per tag (so the user can drop the
  // irrelevant ones); a term that matched nothing shows a single filename chip.
  (filter.include ?? []).forEach((t, i) => {
    const matched = t.matchedTags ?? [];
    if (matched.length > 0) {
      for (const mt of matched) {
        chips.push({
          id: `include:${i}:tag:${mt.slug}`,
          label: mt.name,
          tone: "include",
          remove: (f) => ({ ...f, include: dropMatchedTag(f.include, i, mt.slug) }),
        });
      }
    } else {
      chips.push({
        id: `include:${i}:${t.term}`,
        label: `${t.term} (name)`,
        tone: "include",
        remove: (f) => ({ ...f, include: (f.include ?? []).filter((_, j) => j !== i) }),
      });
    }
  });
  (filter.exclude ?? []).forEach((t, i) => {
    const matched = t.matchedTags ?? [];
    if (matched.length > 0) {
      for (const mt of matched) {
        chips.push({
          id: `exclude:${i}:tag:${mt.slug}`,
          label: `excluding ${mt.name}`,
          tone: "exclude",
          remove: (f) => ({ ...f, exclude: dropMatchedTag(f.exclude, i, mt.slug) }),
        });
      }
    } else {
      chips.push({
        id: `exclude:${i}:${t.term}`,
        label: `excluding ${t.term} (name)`,
        tone: "exclude",
        remove: (f) => ({ ...f, exclude: (f.exclude ?? []).filter((_, j) => j !== i) }),
      });
    }
  });
  (filter.name?.include ?? []).forEach((n, i) => {
    chips.push({
      id: `name:inc:${i}`,
      label: `name: "${n}"`,
      tone: "include",
      remove: (f) => removeName(f, "include", i),
    });
  });
  (filter.name?.exclude ?? []).forEach((n, i) => {
    chips.push({
      id: `name:exc:${i}`,
      label: `excluding name "${n}"`,
      tone: "exclude",
      remove: (f) => removeName(f, "exclude", i),
    });
  });
  if (filter.favorites) {
    chips.push({
      id: "favorites",
      label: "favorited",
      tone: "favorite",
      remove: (f) => ({ ...f, favorites: undefined }),
    });
  }
  if (filter.orientation) {
    chips.push({
      id: "orientation",
      label: filter.orientation,
      tone: "neutral",
      remove: (f) => ({ ...f, orientation: undefined }),
    });
  }
  if (filter.minWidth || filter.minHeight) {
    chips.push({
      id: "dimensions",
      label: `≥ ${filter.minWidth ?? "?"}×${filter.minHeight ?? "?"}`,
      tone: "neutral",
      remove: (f) => ({ ...f, minWidth: undefined, minHeight: undefined }),
    });
  }
  if (filter.exif) {
    if (filter.exif.cameraMake) {
      chips.push({
        id: "exif:make",
        label: `make: ${filter.exif.cameraMake}`,
        tone: "exif",
        remove: (f) => ({ ...f, exif: { ...f.exif, cameraMake: undefined } }),
      });
    }
    if (filter.exif.cameraModel) {
      chips.push({
        id: "exif:model",
        label: `camera: ${filter.exif.cameraModel}`,
        tone: "exif",
        remove: (f) => ({ ...f, exif: { ...f.exif, cameraModel: undefined } }),
      });
    }
    if (filter.exif.lensModel) {
      chips.push({
        id: "exif:lens",
        label: `lens: ${filter.exif.lensModel}`,
        tone: "exif",
        remove: (f) => ({ ...f, exif: { ...f.exif, lensModel: undefined } }),
      });
    }
    if (filter.exif.location) {
      chips.push({
        id: "exif:loc",
        label: `location: ${filter.exif.location}`,
        tone: "exif",
        remove: (f) => ({ ...f, exif: { ...f.exif, location: undefined } }),
      });
    }
  }
  if (filter.ai) {
    if (filter.ai.model) {
      chips.push({
        id: "ai:model",
        label: `ai model: ${filter.ai.model}`,
        tone: "exif",
        remove: (f) => ({ ...f, ai: { ...f.ai, model: undefined } }),
      });
    }
    if (filter.ai.prompt) {
      chips.push({
        id: "ai:prompt",
        label: `ai prompt: ${filter.ai.prompt}`,
        tone: "exif",
        remove: (f) => ({ ...f, ai: { ...f.ai, prompt: undefined } }),
      });
    }
    if (filter.ai.lora) {
      chips.push({
        id: "ai:lora",
        label: `lora: ${filter.ai.lora}`,
        tone: "exif",
        remove: (f) => ({ ...f, ai: { ...f.ai, lora: undefined } }),
      });
    }
  }
  (filter.warnings ?? []).forEach((w, i) => {
    chips.push({
      id: `warn:${i}`,
      label: w,
      tone: "warning",
      remove: (f) => ({ ...f, warnings: (f.warnings ?? []).filter((_, j) => j !== i) }),
    });
  });
  return chips;
}
